import { selectMyCommunities } from 'core/selectors';
import { useServices } from 'core/services';
import { BaseHttpError } from 'core/services/api/errors';
import { CommunitiesResponse } from 'core/services/api/response-types';
import { Community } from 'core/types';
import { getPageParams } from 'core/utils/pagination';
import { communityKeys } from 'core/utils/query-key-factory';
import {
	useInfiniteQuery,
	useQuery,
	InfiniteData,
} from '@tanstack/react-query';
import { useCurrentIdentity } from 'core/uses-cases/authentication';

type Options = { enabled: boolean };
export const useGetMyCommunities = (options: Options = { enabled: true }) => {
	const { api, authentication } = useServices();
	const identity = useCurrentIdentity();

	const isLoggedIn = authentication.isAuthenticated();

	const isEnabled = !!identity && isLoggedIn && options.enabled;

	return useInfiniteQuery<
		CommunitiesResponse,
		BaseHttpError,
		InfiniteData<Community[]>
	>({
		queryKey: communityKeys.list('my_communities'),
		queryFn: ({ pageParam = undefined, signal }) => {
			return api.getMyCommunities(identity?.uuid ?? '', undefined, {
				pagination: pageParam as any,
				signal,
			});
		},
		initialPageParam: undefined,
		enabled: isEnabled,
		staleTime: 1000 * 60 * 5, // 5 minutes,
		select: (response: InfiniteData<CommunitiesResponse>) => {
			try {
				const result: InfiniteData<Community[]> = {
					pages: response.pages.map(page => {
						return selectMyCommunities(page.data || []);
					}),
					pageParams: response.pageParams,
				};

				return result;
			} catch (error) {
				console.error('[useGetMyCommunities] Error in select:', error);
				console.error('[useGetMyCommunities] Response:', response);
				throw error;
			}
		},
		getPreviousPageParam: firstPage =>
			getPageParams(firstPage.pagination).previousParam,
		getNextPageParam: lastPage =>
			getPageParams(lastPage.pagination).nextParam,
		refetchOnMount: true,
	});
};

export const useGetInvitedCommunities = (
	options: Options = { enabled: true }
) => {
	const { api, authentication } = useServices();
	const identity = useCurrentIdentity();
	const isLoggedIn = authentication.isAuthenticated();

	const isEnabled = !!identity && isLoggedIn && options.enabled;

	return useQuery<CommunitiesResponse, BaseHttpError, Community[]>({
		queryKey: communityKeys.list('invited'),
		queryFn: ({ signal }) => {
			return api.getMyCommunities(identity?.uuid ?? '', 'invited', {
				signal,
			});
		},
		enabled: isEnabled,
		staleTime: 1000 * 60 * 5, // 5 minutes,
		select: response => {
			try {
				return selectMyCommunities(response.data || []);
			} catch (error) {
				console.error(
					'[useGetInvitedCommunities] Error in select:',
					error
				);
				console.error('[useGetInvitedCommunities] Response:', response);
				throw error;
			}
		},
		refetchOnMount: true,
	});
};

export const useGetPendingCommunities = (
	options: Options = { enabled: true }
) => {
	const { api, authentication } = useServices();
	const identity = useCurrentIdentity();
	const isLoggedIn = authentication.isAuthenticated();

	const isEnabled = !!identity && isLoggedIn && options.enabled;

	return useQuery<CommunitiesResponse, BaseHttpError, Community[]>({
		queryKey: communityKeys.list('pending'),
		queryFn: ({ signal }) => {
			return api.getMyCommunities(identity?.uuid ?? '', 'pending', {
				signal,
			});
		},
		enabled: isEnabled,
		staleTime: 1000 * 60 * 5, // 5 minutes,
		select: response => {
			try {
				return selectMyCommunities(response.data || []);
			} catch (error) {
				console.error(
					'[useGetPendingCommunities] Error in select:',
					error
				);
				console.error('[useGetPendingCommunities] Response:', response);
				throw error;
			}
		},
		refetchOnMount: true,
	});
};
